import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";

function formatDate(date) {
  if (!date) return "-";

  const d = new Date(date);

  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

const money = (value) =>
  Number(value || 0).toLocaleString("fr-FR");

export default function HistoriqueAppartement() {

  const { id } = useParams();
  const navigate = useNavigate();

  const [sejours, setSejours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("tous");

  useEffect(() => {

    const loadHistorique = async () => {

      try {

        setLoading(true);

        const res = await api.get(
          `/sejours/appartement/${id}`
        );

        setSejours(res.data);

      } catch (err) {

        console.error(
          "ERREUR HISTORIQUE:",
          err
        );

      } finally {

        setLoading(false);

      }

    };

    if (id) {
      loadHistorique();
    }

  }, [id]);

  const filteredSejours = useMemo(() => {

    return sejours.filter((s) => {

      const nom = `
        ${s.nom || ""}
        ${s.post_nom || ""}
        ${s.prenom || ""}
      `.toLowerCase();

      const rechercheOK =
        nom.includes(search.toLowerCase());

      if (!rechercheOK) {
        return false;
      }

      switch (filter) {

        case "encours":
          return s.statut === "en_cours";

        case "termines":
          return s.statut === "termine";

        case "annules":
          return s.statut === "annule";

        case "expires":
          return s.statut === "expire";

        case "payes":
          return Number(s.reste || 0) === 0;

        case "partiels":
          return (
            Number(s.total_paye || 0) > 0 &&
            Number(s.reste || 0) > 0
          );

        case "impayes":
          return Number(s.total_paye || 0) === 0;

        default:
          return true;

      }

    });

  }, [sejours, search, filter]);

  return (

    <div className="min-h-screen bg-gray-50">

      {/* ================= HEADER ================= */}

      <div className="bg-white border-b">

        <div className="max-w-6xl mx-auto px-6 py-5">

          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 hover:text-gray-800 mb-3"
          >
            ← Retour
          </button>

          <div className="flex items-center justify-between">

            <div>

              <h1 className="text-xl font-semibold text-gray-900">
                Historique des séjours
              </h1>

              <p className="text-sm text-gray-500 mt-1">
                Consultez les séjours passés et actuels de cet appartement
              </p>

            </div>

            <div className="text-sm text-gray-500">
              {sejours.length} séjour(s)
            </div>

          </div>

        </div>

      </div>


      {/* ================= CONTENU ================= */}

      <main className="max-w-6xl mx-auto px-6 py-6">

        {/* RECHERCHE */}

        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">

          <div className="flex flex-col lg:flex-row gap-4">

            {/* Recherche */}
            <div className="flex-1">

              <input
                type="text"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                placeholder="Rechercher un client..."
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />

            </div>

            {/* Filtres */}
            <div className="overflow-x-auto">

              <div className="flex gap-2">

                {[
                  ["tous", "Tous"],
                  ["encours", "En cours"],
                  ["termines", "Terminés"],
                  ["annules", "Annulés"],
                  ["expires", "Expirés"],
                  ["payes", "Payés"],
                  ["partiels", "Partiels"],
                  ["impayes", "Impayés"]
                ].map(([value, label]) => (

                  <button
                    key={value}
                    onClick={() => setFilter(value)}
                    className={`
                      px-3 py-2 rounded-lg text-xs font-medium
                      whitespace-nowrap transition
                      ${
                        filter === value
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }
                    `}
                  >
                    {label}
                  </button>

                ))}

              </div>

            </div>

          </div>

        </div>

        {/* ================= LISTE ================= */}

        <div className="bg-white border rounded-xl overflow-hidden">

          {loading ? (

            <div className="p-10 text-center text-gray-500">
              Chargement de l'historique...
            </div>

          ) : filteredSejours.length === 0 ? (

            <div className="p-10 text-center text-gray-500">
              Aucun séjour trouvé.
            </div>

          ) : (

            <div className="divide-y">


              <div className="hidden lg:grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 border-b text-[11px] font-semibold uppercase tracking-wide text-gray-400">

                <div className="col-span-3">
                  Client
                </div>

                <div className="col-span-3">
                  Séjour
                </div>

                <div className="col-span-2">
                  Statut
                </div>

                <div className="col-span-3">
                  Finances
                </div>

                <div className="col-span-1 text-right">
                  Action
                </div>

              </div>

              {filteredSejours.map((s) => {

                const total =
                  Number(s.total_sejour || 0);

                const paye =
                  Number(s.total_paye || 0);

                const reste =
                  Math.max(
                    0,
                    total - paye
                  );

                const tropPercu =
                  Number(s.trop_percu || 0);


                const statutLabel = {
                  reserve: "Réservé",
                  confirme: "Confirmé",
                  en_cours: "En cours",
                  termine: "Terminé",
                  annule: "Annulé",
                  expire: "Expiré",
                  no_show: "No-show"
                }[s.statut] || s.statut;


                return (

                  <div
                    key={s.id}
                    className="px-5 py-3.5 hover:bg-gray-50 transition"
                  >

                    <div className="grid grid-cols-12 gap-4 items-center">

                      {/* CLIENT */}

                      <div className="col-span-3">

                        <div className="font-medium text-gray-900 truncate">
                          {s.nom} {s.post_nom} {s.prenom}
                        </div>

                        <div className="text-[11px] text-gray-400 mt-1">
                          Séjour #{s.id}
                          {s.source === "public" ? " · Site" : " · Interne"}
                        </div>

                      </div>


                      {/* DATES */}

                      <div className="col-span-3">

                        <div className="text-sm text-gray-700">
                          {formatDate(s.date_debut)}
                        </div>

                        <div className="text-[11px] text-gray-400 my-0.5">
                          jusqu'au
                        </div>

                        <div className="text-sm text-gray-700">
                          {formatDate(s.date_fin_prevue)}
                        </div>

                      </div>


                      {/* STATUT */}

                      <div className="col-span-2">

                        <span
                          className={`
                            inline-flex px-3 py-1
                            rounded-full text-xs font-semibold
                            ${
                              s.statut === "annule"
                                ? "bg-red-100 text-red-700"
                                : s.statut === "termine"
                                ? "bg-gray-100 text-gray-700"
                                : s.statut === "en_cours"
                                ? "bg-blue-100 text-blue-700"
                                : s.statut === "confirme"
                                ? "bg-green-100 text-green-700"
                                : s.statut === "reserve"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-gray-100 text-gray-600"
                            }
                          `}
                        >
                          {statutLabel}
                        </span>

                      </div>


                      {/* FINANCES */}

                      <div className="col-span-3">

                        <div className="flex items-center gap-5">

                          <div>
                            <div className="text-[10px] uppercase text-gray-400">
                              Total
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                              {money(total)} $
                            </div>
                          </div>

                          <div>
                            <div className="text-[10px] uppercase text-gray-400">
                              Payé
                            </div>
                            <div className="text-sm font-semibold text-green-700">
                              {money(paye)} $
                            </div>
                          </div>

                          <div>
                            <div className="text-[10px] uppercase text-gray-400">
                              {tropPercu > 0 ? "Trop-perçu" : "Reste"}
                            </div>

                            <div
                              className={
                                tropPercu > 0
                                  ? "text-sm font-semibold text-orange-600"
                                  : "text-sm font-semibold text-red-600"
                              }
                            >
                              {money(
                                tropPercu > 0
                                  ? tropPercu
                                  : reste
                              )} $
                            </div>
                          </div>

                        </div>

                      </div>


                      {/* ACTION */}

                      <div className="col-span-1 flex justify-end">

                        <button
                          onClick={() =>
                            navigate(
                              `/sejours/${s.id}`
                            )
                          }
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 transition"
                        >
                          Voir
                        </button>

                      </div>

                    </div>

                  </div>

                );

              })}

            </div>

          )}

        </div>

      </main>

    </div>

  );
}